import type { CustomFile, TypeUploadBody } from '../types';

import type { TypeUploadConfig, TypeUploadOnceResult, TypeResolvedFeatures } from './types';
import type { TypeUploadContext } from './createUploadContext';
import type { TypeRequestRegistry } from './createRequestRegistry';
import type { TypeUploadFeature } from './core/types';
import { EmptyUploadError } from './errors';

import sendRequest from './core/sendRequest';
import getBodySize from '../utils/getBodySize';

// ----------------------------------------------------------------------------

export type TypeUploadOnceBatchDeps = {
    config: TypeUploadConfig;
    features: TypeResolvedFeatures;
    plugins: TypeUploadFeature[];
    context: TypeUploadContext;
    registry: TypeRequestRegistry;
    progressMap: { file: CustomFile; loaded: number }[];
    totalBytes: number;
    fnEmitProgress: (evt: ProgressEvent | null, totalPercent: number) => void;
};

// ----------------------------------------------------------------------------

/*
 * Builds the single body carrying every file: the caller's `fnBody` when given, otherwise
 * a FormData with one `files[]` entry per prepared file.
 */
async function buildBatchBody(activeFiles: CustomFile[], config: TypeUploadConfig): Promise<TypeUploadBody> {
    // If using function body
    if (typeof config.fnBody === 'function') {
        const builtBody = await config.fnBody(activeFiles);
        const size = getBodySize(builtBody);

        if (!builtBody || (!(builtBody instanceof FormData) && size === 0)) {
            throw new EmptyUploadError('Body for batch upload is empty');
        }

        return builtBody;
    }

    const form = new FormData();

    for (const file of activeFiles) {
        const preparedFile = typeof config.fnPrepareFile === 'function' ? await config.fnPrepareFile(file) : file;

        if (preparedFile.size === 0) {
            throw new EmptyUploadError(`Prepared file "${file.name}" is empty`);
        }

        form.append('files[]', preparedFile, preparedFile instanceof File ? preparedFile.name : file.name);
    }

    return form;
}

/*
 * The batch counterpart of uploadOnceFile: one request for the whole set, with the single upload
 * stream split across the files by each one's share of the total bytes. Same transport and same
 * features as the per-file path — only the body and the progress mapping differ.
 */
async function uploadOnceBatch(
    activeFiles: CustomFile[],
    deps: TypeUploadOnceBatchDeps,
): Promise<TypeUploadOnceResult> {
    const { config, features, plugins, context, registry, progressMap, totalBytes, fnEmitProgress } = deps;

    if (context.abortedFlag.aborted) {
        return { ok: false, status: 0, reason: 'abort' };
    }

    let body: TypeUploadBody;

    try {
        body = await buildBatchBody(activeFiles, config);
    } catch (err: any) {
        if (context.abortedFlag.aborted) {
            return { ok: false, status: 0, reason: 'abort' };
        }

        // Deterministic body failures (empty file/body) are permanent; others may be transient.
        return {
            ok: false,
            status: 0,
            reason: 'error',
            retryable: !(err instanceof EmptyUploadError),
            message: err?.message ?? 'Failed to prepare batch upload',
        };
    }

    return sendRequest({
        config,
        features,
        plugins,
        context,
        registry,
        body,
        label: `${activeFiles.length} files`,
        fnUploadProgress: (evt): void => {
            const totalPercent = evt.total ? (evt.loaded / evt.total) * 100 : 0;
            const globalPercent = evt.total ? evt.loaded / evt.total : 0;
            let accumulated = 0;

            for (const p of progressMap) {
                const start = totalBytes ? accumulated / totalBytes : 0;
                const end = totalBytes ? (accumulated + p.file.size) / totalBytes : 0;

                /*
                 * Only ever increases. A retry restarts the single request from zero, and batch
                 * mode emits no fnFileRetry, so a regressing share would reset the caller's
                 * per-file display with nothing to explain it.
                 */
                if (globalPercent >= end) {
                    p.loaded = 100;
                } else if (globalPercent > start && end > start) {
                    p.loaded = Math.max(p.loaded, ((globalPercent - start) / (end - start)) * 100);
                }

                accumulated += p.file.size;
            }

            fnEmitProgress(evt, totalPercent);
        },
    });
}

// ----------------------------------------------------------------------------

export default uploadOnceBatch;
